import { useEffect, useState } from "react";
import {
 FaAward,
 FaBullseye,
 FaCheckCircle,
 FaEye,
 FaIndustry,
 FaShieldAlt,
} from "react-icons/fa";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import SectionTitle from "../../components/ui/SectionTitle";
import Modal from "../../components/ui/Modal";
import { getAboutSliders } from "../../services/aboutSliderService";
import { getHomeImages } from "../../services/homeContentService";
import { getCertifications } from "../../services/certificationService";

const makeCertificateImage = (name, type) => {
 const title = name.replace(/&/g, "and");
 const accent = type === "system" ? "#0b3768" : "#18A84A";
 const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="980" height="680" viewBox="0 0 980 680">
  <rect width="980" height="680" fill="#ffffff"/>
  <rect x="42" y="42" width="896" height="596" rx="24" fill="#f6fbf8" stroke="#dcebe3" stroke-width="4"/>
  <rect x="78" y="78" width="824" height="524" rx="18" fill="#ffffff" stroke="${accent}" stroke-width="3"/>
  <text x="490" y="170" text-anchor="middle" font-family="Arial, sans-serif" font-size="26" font-weight="700" fill="#64748b" letter-spacing="4">MOZO EV</text>
  <text x="490" y="245" text-anchor="middle" font-family="Arial, sans-serif" font-size="52" font-weight="800" fill="#062B3A">${title}</text>
  <text x="490" y="310" text-anchor="middle" font-family="Arial, sans-serif" font-size="30" font-weight="700" fill="${accent}">Certification</text>
  <line x1="180" y1="380" x2="800" y2="380" stroke="#dcebe3" stroke-width="3"/>
  <text x="490" y="445" text-anchor="middle" font-family="Arial, sans-serif" font-size="22" font-weight="600" fill="#31515B">Quality, safety and compliance standard</text>
  <text x="490" y="505" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="#64748b">Tap close to return to the catalogue</text>
 </svg>`;
 return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeCertificationLogo = (name, type) => {
 const isIso = name.startsWith("ISO") || name.startsWith("IATF");
 const accent = type === "system" ? "#0b3768" : "#111827";
 const subtitle = isIso ? name : "STANDARD";
 const main = isIso ? "ISO" : name;
 const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240">
  <rect width="240" height="240" rx="18" fill="#ffffff"/>
  <rect x="10" y="10" width="220" height="220" rx="16" fill="#ffffff" stroke="#dcebe3" stroke-width="3"/>
  ${isIso ? `<circle cx="120" cy="92" r="45" fill="none" stroke="${accent}" stroke-width="4" opacity="0.7"/><path d="M78 92h84M120 48c18 20 18 68 0 88M120 48c-18 20-18 68 0 88" stroke="${accent}" stroke-width="4" fill="none" opacity="0.7"/>` : ""}
  <text x="120" y="118" text-anchor="middle" font-family="Arial, sans-serif" font-size="${main.length > 5 ? 38 : 54}" font-weight="900" fill="${accent}">${main}</text>
  <text x="120" y="165" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#42566B">${subtitle}</text>
 </svg>`;
 return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const systemCertifications = [
 "ISO 9001",
 "ISO 14001",
 "ISO 45001",
 "ISO 14064",
 "IATF 16949",
];

const productCertifications = [
 "CE",
 "CB",
 "CQC",
 "GB",
 "UL",
 "KC",
 "EMC",
 "IEC",
 "UN38.3",
 "IATA",
 "BIS",
 "PSE",
 "BSMI",
];

const timeline = [
 ["2009", "Started as a battery and power electronics supply desk."],
 [
  "2014",
  "Expanded into lithium cells, chargers, connectors and pack accessories.",
 ],
 ["2019", "Built a dealer-first catalogue process for EV battery quotations."],
 ["2026", "Modern digital catalogue for premium B2B EV product discovery."],
];

const fallbackAboutImage =
 "https://mozo-store.s3.ap-south-1.amazonaws.com/categories/1785833396294-0-whatsapp-image-2026-08-04-at-12-02-36-pm-jpeg.webp";
const fallbackJourneyImages = [
 {
  id: "journey-1",
  title: "EV Scooty",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/categories/1788361826662-0-02-png.webp",
 },
 {
  id: "journey-2",
  title: "E-Rickshaw Battery",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/categories/1786977661964-0-banner-jpg.webp",
 },
 {
  id: "journey-3",
  title: "Battery LFP",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/categories/1786007186337-0-box-images-jpg.webp",
 },
 {
  id: "journey-4",
  title: "Battery NMC",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/categories/1786007211284-0-chatgpt-image-aug-6-2026-02-35-36-pm-png.webp",
 },
];

const fallbackCommunityImages = [
 {
  id: "community-1",
  title: "Battery 48x30",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1784964435459-1-1-jpg.webp",
 },
 {
  id: "community-2",
  title: "Battery 48x45",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1784965401538-1-1-jpg.webp",
 },
 {
  id: "community-3",
  title: "Battery 60x30",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1784971938965-1-1-jpg.webp",
 },
 {
  id: "community-4",
  title: "Battery 60x45",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1784972289757-1-1-jpg.webp",
 },
 {
  id: "community-5",
  title: "Battery Metal Box 48x30",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1786710485313-1-01-0000-whatsapp-image-2026-08-14-at-3-33-37-pm-jpg.webp",
 },
 {
  id: "community-6",
  title: "Battery Metal Box 48x45",
  image:
   "https://mozo-store.s3.ap-south-1.amazonaws.com/products/1786710525791-1-01-0001-whatsapp-image-2026-08-14-at-5-06-23-pm-1-jpg.webp",
 },
];

const stats = [
 ["15+", "Years Experience"],
 ["2,400+", "Orders Served"],
 ["8,500+", "Happy Customers"],
 ["1,000+", "EV Products"],
];

const strengths = [
 "Wide catalogue across cells, BMS, chargers, machines, connectors and accessories.",
 "Manual quote handling for stock, bulk quantity, cell grade and delivery clarity.",
 "Brand-led product discovery with specification-first cards.",
 "Future-ready services layer for backend integration.",
];

export default function About() {
 const [activeCertificate, setActiveCertificate] = useState(null);
 const [journeyImages, setJourneyImages] = useState(fallbackJourneyImages);
 const [communityImages, setCommunityImages] = useState(
  fallbackCommunityImages,
 );
 const [systemItems, setSystemItems] = useState(systemCertifications);
 const [productItems, setProductItems] = useState(productCertifications);

 useEffect(() => {
  Promise.all([getHomeImages("journey"), getHomeImages("community")]).then(
   ([journeyData, communityData]) => {
    if (journeyData) setJourneyImages(journeyData);
    if (communityData) setCommunityImages(communityData);
   },
  );
 }, []);
 useEffect(() => {
  Promise.all([getCertifications("system"), getCertifications("product")])
   .then(([systemData, productData]) => {
    if (systemData?.length) setSystemItems(systemData);
    if (productData?.length) setProductItems(productData);
   })
   .catch(() => {});
 }, []);

 return (
  <main className="bg-base/60">
   <section className="container-pad py-8 sm:py-10 lg:py-12">
    <Breadcrumb items={[{ label: "About Us" }]} />

    <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)] lg:items-center">
     <div className="max-w-3xl">
      <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-primary">
       About Mozo EV
      </p>
      <h1 className="mt-4 text-3xl font-extrabold leading-tight text-dark sm:text-4xl lg:text-5xl">
       Premium EV battery products partner for modern mobility businesses
      </h1>
      <p className="mt-5 text-base leading-8 text-body sm:text-lg">
       Mozo EV helps EV workshops, battery pack makers, dealers and solar
       storage teams explore reliable products and request clear,
       requirement-ready quotations.
      </p>

      <div className="mt-7 grid gap-4 sm:grid-cols-2">
       <Value
        icon={FaBullseye}
        title="Mission"
        text="Make EV battery product discovery simpler, cleaner and more dependable for B2B buyers."
       />
       <Value
        icon={FaEye}
        title="Vision"
        text="Become the most trusted digital catalogue for lithium battery products and pack supply."
       />
      </div>
     </div>

     <AboutImageSlider />
    </div>
   </section>

   <section className="bg-white py-8 sm:py-10">
    <div className="container-pad grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
     {stats.map(([value, label]) => (
      <div key={label} className="border-l-4 border-primary bg-base px-5 py-4">
       <strong className="block text-3xl font-extrabold text-dark sm:text-4xl">
        {value}
       </strong>
       <span className="mt-1 block text-sm font-bold uppercase tracking-wide text-body">
        {label}
       </span>
      </div>
     ))}
    </div>
   </section>

   <CertificationSection
    systemItems={systemItems}
    productItems={productItems}
    onSelect={setActiveCertificate}
   />

   <Modal
    open={Boolean(activeCertificate)}
    title={activeCertificate?.name || "Certificate"}
    onClose={() => setActiveCertificate(null)}
   >
    {activeCertificate ? (
     <img
      src={activeCertificate.image}
      alt={`${activeCertificate.name} certificate`}
      className="max-h-[72vh] w-full rounded-lg border border-line object-contain"
     />
    ) : null}
   </Modal>

   <ImageMarqueeSection
    eyebrow="Our journey"
    title="Our journey in numbers"
    images={journeyImages}
    variant="wide"
   />

   <section className="container-pad grid gap-8 py-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
    <div>
     <SectionTitle eyebrow="Why choose us" title="Built for EV procurement" />
     <div className="grid gap-3">
      {strengths.map((item) => (
       <div
        key={item}
        className="flex gap-3 border border-line bg-white p-4 shadow-sm"
       >
        <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center bg-emerald-50 text-primary">
         <FaCheckCircle />
        </span>
        <p className="font-semibold leading-7 text-body">{item}</p>
       </div>
      ))}
     </div>
    </div>

    <div>
     <SectionTitle eyebrow="Timeline" title="Our journey" />
     <div className="relative grid gap-4 before:absolute before:left-4 before:top-3 before:h-[calc(100%-24px)] before:w-px before:bg-line">
      {timeline.map(([year, text]) => (
       <div key={year} className="relative grid grid-cols-[2rem_1fr] gap-4">
        <span className="relative z-10 mt-1 grid h-8 w-8 place-items-center rounded-full bg-primary text-[10px] font-black text-white">
         {year.slice(2)}
        </span>
        <div className="border border-line bg-white p-5 shadow-sm">
         <p className="text-2xl font-extrabold text-primary">{year}</p>
         <p className="mt-2 font-semibold leading-7 text-body">{text}</p>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   <ImageMarqueeSection
    eyebrow="Join the Mozo community"
    title="Join the Mozo community"
    images={communityImages}
    variant="gallery"
    reverse
   />

   <section className="container-pad pb-14">
    <div className="grid gap-6 bg-dark p-6 text-white shadow-lift sm:p-8 lg:grid-cols-[auto_1fr_auto] lg:items-center">
     <span className="grid h-14 w-14 place-items-center bg-white/10 text-3xl text-primary">
      <FaAward />
     </span>
     <div>
      <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-primary">
       Achievements
      </p>
      <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">
       Trusted for fast enquiry handling and organized EV product discovery
      </h2>
      <p className="mt-3 max-w-4xl leading-7 text-slate-300">
       Recognized by customers for practical support across battery pack,
       repair, dealer and ESS procurement workflows.
      </p>
     </div>
     <FaIndustry className="hidden text-5xl text-white/20 lg:block" />
    </div>
   </section>
  </main>
 );
}

function CertificationSection({ systemItems, productItems, onSelect }) {
 return (
  <section className="bg-[#f2f5f9] py-12 sm:py-14">
   <div className="container-pad">
    <div className="mx-auto max-w-3xl text-center">
     <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-primary">
      Certification & Standards
     </p>
     <h2 className="mt-3 text-3xl font-extrabold text-dark sm:text-4xl">
      Certification & Standards
     </h2>
    </div>
    <CertificationGroup
     title="System Certifications"
     type="system"
     items={systemItems}
     onSelect={onSelect}
    />
    <CertificationGroup
     title="Product Certifications"
     type="product"
     items={productItems}
     onSelect={onSelect}
    />
   </div>
  </section>
 );
}

function CertificationGroup({ title, type, items, onSelect }) {
 return (
  <div className="mt-10">
   <div className="mb-5 flex items-center gap-3">
    <span className="grid h-9 w-9 place-items-center bg-white text-primary shadow-sm">
     <FaShieldAlt />
    </span>
    <h3 className="text-xl font-extrabold text-dark">{title}</h3>
   </div>
   <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10">
    {items.map((item) => {
     const name = typeof item === "string" ? item : item.name;
     const logo =
      typeof item === "string" ? makeCertificationLogo(name, type) : item.logo;
     const image =
      typeof item === "string"
       ? makeCertificateImage(name, type)
       : item.certificateImage;
     return (
      <button
       key={typeof item === "string" ? name : item.id}
       type="button"
       onClick={() => onSelect({ name, image })}
       className="group aspect-square overflow-hidden border border-line bg-white shadow-sm transition hover:-translate-y-1 hover:border-primary hover:shadow-soft focus:outline-none focus:ring-2 focus:ring-primary/30"
       aria-label={`View ${name} certificate`}
      >
       <img
        src={logo}
        alt=""
        className="h-full w-full object-cover transition group-hover:scale-105"
        loading="lazy"
       />
      </button>
     );
    })}
   </div>
  </div>
 );
}

function ImageMarqueeSection({
 eyebrow,
 title,
 images,
 variant = "wide",
 reverse = false,
}) {
 const uniqueImages = images.filter(
  (image, index, list) =>
   image?.image &&
   list.findIndex((item) => item.image === image.image) === index,
 );
 if (!uniqueImages.length) return null;

 const displayImages =
  uniqueImages.length > 1 ? [...uniqueImages, ...uniqueImages] : uniqueImages;

 return (
  <section className="image-marquee-section py-12 sm:py-14">
   <div className="container-pad text-center">
    <SectionTitle eyebrow={eyebrow} title={title} align="center" />
   </div>
   <div className={`image-marquee mt-8 ${variant} ${reverse ? "reverse" : ""}`}>
    <div className="image-marquee-track">
     {displayImages.map((image, index) => (
      <figure
       className="image-marquee-card"
       key={`${image.id || image.image}-${index}`}
      >
       <img src={image.image} alt={image.title || ""} loading="lazy" />
      </figure>
     ))}
    </div>
   </div>
  </section>
 );
}

function AboutImageSlider() {
 const [images, setImages] = useState([fallbackAboutImage]);
 const [activeIndex, setActiveIndex] = useState(0);

 useEffect(() => {
  let alive = true;
  getAboutSliders().then((slides) => {
   if (!alive) return;
   const sliderImages = slides
    .map((slide) => slide.image || slide.imageUrl)
    .filter(Boolean);
   setImages(sliderImages.length ? sliderImages : [fallbackAboutImage]);
   setActiveIndex(0);
  });
  return () => {
   alive = false;
  };
 }, []);

 useEffect(() => {
  if (images.length <= 1) return undefined;
  const timer = window.setInterval(() => {
   setActiveIndex((index) => (index + 1) % images.length);
  }, 3000);
  return () => window.clearInterval(timer);
 }, [images.length]);

 return (
  <div className="relative overflow-hidden border border-line bg-white shadow-lift">
   <div className="aspect-[4/3] min-h-[280px] sm:min-h-[360px] lg:min-h-[430px]">
    {images.map((image, index) => (
     <div
      key={`${image}-${index}`}
      className={`absolute inset-0 h-full w-full transition-opacity duration-500 ${
       index === activeIndex ? "opacity-100" : "opacity-0"
      }`}
     >
      <img
       className="absolute inset-0 h-full w-full object-cover"
       src={image}
       alt="Mozo EV catalogue slider"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-dark/55 via-transparent to-transparent" />
     </div>
    ))}
   </div>
   <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
    <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-200">
     Product Catalogue
    </p>
    <h2 className="mt-1 text-2xl font-extrabold">
     Built around real EV supply
    </h2>
   </div>
   {images.length > 1 ? (
    <div className="absolute right-4 top-4 flex gap-2 bg-dark/25 px-3 py-2 backdrop-blur">
     {images.map((image, index) => (
      <button
       key={`dot-${image}-${index}`}
       type="button"
       onClick={() => setActiveIndex(index)}
       className={`h-2 rounded-full transition-all ${
        index === activeIndex ? "w-6 bg-white" : "w-2 bg-white/60"
       }`}
       aria-label={`Show about slide ${index + 1}`}
      />
     ))}
    </div>
   ) : null}
  </div>
 );
}

function Value({ icon: Icon, title, text }) {
 return (
  <div className="border border-line bg-white p-5 shadow-soft">
   <div className="flex items-start gap-4">
    <span className="grid h-11 w-11 shrink-0 place-items-center bg-emerald-50 text-xl text-primary">
     <Icon />
    </span>
    <div>
     <h3 className="text-xl font-extrabold text-dark">{title}</h3>
     <p className="mt-2 leading-7 text-body">{text}</p>
    </div>
   </div>
  </div>
 );
}
